import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useApplication from '@/hooks/useApplication';
import { ApplicationProps } from '@/models/Application';
import { ApplicationStatus } from '@/utils/constants';

/**
 * Dependencies that the hook needs from the component.
 * Keep these as plain values/functions so the hook stays testable and reusable.
 */
type UseApproveApplicationProps = {
  currentApplication: ApplicationProps;
  canEdit: boolean;
  shippingContactsNeeded: boolean;
  selectedContactId: number | null;
  additionalStickers: number | null | undefined;
  specialInstructions: string;
  ensureShippingContactId: () => Promise<number | null>;

  isValidShippingSelection: boolean;

  // context updaters you already have access to in the component
  updateCurrentApplication: (app: ApplicationProps) => void;
  updateApplicationStatus: (appData: ApplicationStatus, appId: number) => void;
};

type UseApproveApplicationResult = {
  approveButtonText: string;
  approveButtonEnabled: boolean;
  errorMessage: string;
  isSubmitting: boolean;
  approveApplication: () => Promise<void>;
  resetErrors: () => void;
};

export function useApproveApplication({
  currentApplication,
  canEdit,
  shippingContactsNeeded,
  selectedContactId,
  additionalStickers,
  specialInstructions,
  ensureShippingContactId,
  isValidShippingSelection,
  updateCurrentApplication,
  updateApplicationStatus,
}: UseApproveApplicationProps): UseApproveApplicationResult {
  const navigate = useNavigate();
  const { postApplicationStatus, updateApplication } = useApplication();

  const [approveButtonText, setApproveButtonText] = useState<string>('Approve Application');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const resetErrors = useCallback(() => setErrorMessage(''), []);

  const approveButtonEnabled = useMemo(() => {
    const shippingOk =
      !shippingContactsNeeded || (selectedContactId != null && isValidShippingSelection);

    return !!(canEdit && currentApplication && !isSubmitting && shippingOk);
  }, [
    canEdit,
    currentApplication,
    isSubmitting,
    shippingContactsNeeded,
    selectedContactId,
    isValidShippingSelection,
  ]);

  const approveApplication = useCallback(async () => {
    if (isSubmitting) return;

    setApproveButtonText('Approving...');
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const shippingContactId = await ensureShippingContactId();

      const applicationInfo: ApplicationProps = {
        ...currentApplication,
        specialInstructions: specialInstructions.trim() === '' ? null : specialInstructions,
        shippingContactId: shippingContactId,
        additionalStickers: additionalStickers ?? null,
      } satisfies ApplicationProps;

      const updated = await updateApplication(applicationInfo);
      if (!updated) throw new Error('Failed to update application.');

      let updatedApplicationStatus: ApplicationStatus | null = null;
      try {
        updatedApplicationStatus = await postApplicationStatus(
          Number(currentApplication.id),
          false
        );
      } catch (error: unknown) {
        setErrorMessage('An unexpected error occurred: ' + (error as Error).message);
        setApproveButtonText('Approve Application');
        setIsSubmitting(false);
        return;
      }

      const normalizedSpecial = specialInstructions.trim() === '' ? null : specialInstructions;
      const normalizedStickers = additionalStickers ?? null;

      // Keep local context in sync immediately after successful update
      updateCurrentApplication({
        ...currentApplication,
        shippingContactId,
        specialInstructions: normalizedSpecial,
        additionalStickers: normalizedStickers,
      });

      setApproveButtonText('Application Approved');
      updateApplicationStatus(updatedApplicationStatus, currentApplication.id);

      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (error) {
      // TODO(bug): isSubmitting is never reset here (only in the inner postApplicationStatus
      // catch), so after an ensureShippingContactId/updateApplication failure the Approve button
      // stays disabled and approveApplication() early-returns until reload. Expected:
      // setIsSubmitting(false) here (or in a finally for the non-success paths).
      setErrorMessage('An unexpected error occurred: ' + (error as Error).message);
      setApproveButtonText('Approve Application');
    }
  }, [
    additionalStickers,
    currentApplication,
    ensureShippingContactId,
    navigate,
    postApplicationStatus,
    specialInstructions,
    updateApplication,
    updateApplicationStatus,
    updateCurrentApplication,
    isSubmitting,
  ]);

  return {
    approveButtonText,
    approveButtonEnabled,
    isSubmitting,
    errorMessage,
    approveApplication,
    resetErrors,
  };
}
